// Canny integration helpers.
//
// Loads the Canny JS SDK on demand and identifies the signed-in user so their posts/votes on the
// embedded feedback board are attributed to their Upstand identity (no separate Canny login).
//
// Config is via Vite build-time env vars (both must be set for the feature to light up):
//   VITE_CANNY_APP_ID       - public Canny app id (Canny dashboard → Settings → API)
//   VITE_CANNY_BOARD_TOKEN  - the board to embed (board admin → install settings)
//
// Identity is currently client-side `identify` (public board). If the board is later made private,
// swap to a server-signed SSO token (a `canny-sso` edge function) - `identify` won't admit users to
// a private board on its own.

import { invokeEdgeFn } from './edge-functions';

const APP_ID = ((import.meta.env.VITE_CANNY_APP_ID as string | undefined) ?? '').trim();
const BOARD_TOKEN = ((import.meta.env.VITE_CANNY_BOARD_TOKEN as string | undefined) ?? '').trim();

declare global {
  interface Window {
    Canny?: ((...args: unknown[]) => void) & { q?: unknown[] };
  }
}

/** Both env vars present - the Feedback nav item + view are only shown/usable when true. */
export function cannyConfigured(): boolean {
  return Boolean(APP_ID && BOARD_TOKEN);
}

export function getCannyBoardToken(): string {
  return BOARD_TOKEN;
}

const SDK_ID = 'canny-jssdk';
const SDK_SRC = 'https://sdk.canny.io/sdk.js';

/**
 * Inject the Canny SDK loader once. Mirrors Canny's official snippet: defines `window.Canny` as a
 * synchronous command queue immediately, then async-loads sdk.js which drains the queue - so it's
 * safe to call identify()/render() right after this returns. Idempotent (guards on the script id).
 */
export function loadCannySdk(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (typeof window.Canny !== 'function') {
    const queue = function (...args: unknown[]) {
      (queue.q ||= []).push(args);
    } as ((...args: unknown[]) => void) & { q?: unknown[] };
    queue.q = [];
    window.Canny = queue;
  }
  if (document.getElementById(SDK_ID)) return;
  const first = document.getElementsByTagName('script')[0];
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.src = SDK_SRC;
  script.id = SDK_ID;
  first?.parentNode?.insertBefore(script, first);
}

/**
 * Fetch a server-signed Canny SSO token for the current user (via the canny-sso edge function) so
 * the widget logs them in silently - no "Sign up for Canny" modal. Returns null if SSO isn't
 * configured or the call fails, in which case the caller renders the board anonymously (Canny then
 * shows its own signup). Unsigned client `identify` is not honoured by the embedded widget, so the
 * signed token is the supported path.
 */
export async function fetchCannySsoToken(): Promise<string | null> {
  if (!cannyConfigured()) return null;
  try {
    const res = await invokeEdgeFn<{ ssoToken?: string }>('canny-sso', {});
    return res?.ssoToken ?? null;
  } catch {
    return null;
  }
}

/** Render the embedded feedback board into the on-page `[data-canny]` container. No-op if unconfigured. */
export function renderCannyBoard(ssoToken?: string | null): void {
  if (!cannyConfigured() || typeof window === 'undefined' || typeof window.Canny !== 'function') return;
  window.Canny('render', {
    boardToken: BOARD_TOKEN,
    theme: 'light',
    ...(ssoToken ? { ssoToken } : {}),
  });
}
